import { clsx } from 'clsx';
import { FileText, Upload, X } from 'lucide-react';
import { type DragEvent, type ReactNode, useRef, useState } from 'react';
import { useFieldA11y } from './fields.tsx';
import { Button, iconButtonClass } from './primitives.tsx';

export interface FileDropZoneProps {
  /** Id of the (visually hidden) file input; a surrounding `Field` with the same id labels it. */
  id: string;
  /** The chosen file; the zone shows a file card while it is set. */
  file: File | undefined;
  /** Called with the chosen or dropped file (the first one of a drop), or `undefined` on Remove. */
  onFileChange: (file: File | undefined) => void;
  /** The input's `accept` (MIME types and extensions). A drop is not filtered by it: check the file yourself. */
  accept?: string;
  /** Muted line under the prompt (accepted formats, size limit). */
  hint?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  className?: string;
}

/** `12.4 MB`, `820 KB`, `512 bytes` (binary units, as upload limits are counted). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

/** Lower-case extension of a file name without the dot (`''` when it has none). */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * One-file picker: a dashed drop zone (the whole zone is a button that opens the picker; drag-over changes the
 * border) that becomes a file card (name, size, extension, Replace, Remove) once a file is chosen. The native
 * `<input type="file">` stays the labelled control (visually hidden, out of the tab order); inside a `Field` it
 * picks up `required`, `aria-describedby` and `aria-invalid` like the other ui-kit controls.
 */
export function FileDropZone({
  file,
  onFileChange,
  accept,
  hint,
  disabled,
  className,
  ...a11yProps
}: FileDropZoneProps) {
  const a11y = useFieldA11y(a11yProps);
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const invalid = a11y['aria-invalid'] === true;
  const browse = () => input.current?.click();
  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFileChange(dropped);
  };
  const dragProps = {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      if (!disabled) setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop,
  };
  const border = invalid
    ? 'border-bad'
    : dragging
      ? 'border-accent bg-accent-soft'
      : 'border-control-border hover:border-control-border-hover';
  const ext = file ? fileExtension(file.name) : '';
  return (
    <div className={clsx('w-full min-w-0', className)}>
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        {...(accept ? { accept } : {})}
        disabled={disabled}
        {...a11y}
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          // Cleared so that choosing the same file again (after Remove) fires a change.
          e.target.value = '';
          if (chosen) onFileChange(chosen);
        }}
      />
      {file ? (
        <div
          data-testid="file-card"
          className={clsx(
            'flex w-full min-w-0 items-center gap-3 rounded-lg border bg-surface-2 px-3 py-2.5',
            invalid ? 'border-bad' : dragging ? 'border-accent' : 'border-line',
          )}
          {...dragProps}
        >
          <FileText aria-hidden className="size-5 shrink-0 text-ink-3" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-medium text-ink" title={file.name}>
              {file.name}
            </div>
            <div className="text-[12px] text-ink-3">
              {formatFileSize(file.size)}
              {ext && ` · ${ext.toUpperCase()}`}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={browse} disabled={disabled}>
            Replace
          </Button>
          <button
            type="button"
            aria-label="Remove file"
            className={iconButtonClass('sm')}
            onClick={() => onFileChange(undefined)}
            disabled={disabled}
          >
            <X aria-hidden />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={browse}
          disabled={disabled}
          data-testid="file-drop-zone"
          data-dragging={dragging || undefined}
          className={clsx(
            'flex w-full flex-col items-center gap-2 rounded-lg border-[1.5px] border-dashed bg-surface-2 px-4 py-6 text-center transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-60',
            border,
          )}
          {...dragProps}
        >
          <Upload aria-hidden className="size-6 text-accent" />
          <span className="text-[14.5px] font-medium text-ink">
            Drop a file here or <span className="text-accent-ink underline">browse</span>
          </span>
          {hint && <span className="text-[12.5px] leading-[1.4] text-ink-3">{hint}</span>}
        </button>
      )}
    </div>
  );
}
